import { createElement, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { I18nChildContext } from "@bookingcom/lingojs-react";
import type { SearchCoverageAirlineSeries } from "./SearchCoverageChart";
import { SearchCoverageTooltip } from "./SearchCoverageTooltip";
import { CHART_DATE_TICK_AMOUNT, formatChartDate, formatChartDateAxisLabel } from "../../../utils/chart";
import { AIRLINE_SERIES_COLORS } from "../ChartConstants";

const roundToOneDecimal = (val: number): number => Math.round(val * 10) / 10;

const formatPercent = (val: number): string => `${val.toFixed(1)}%`;

const computeYAxisMax = (series: SearchCoverageAirlineSeries[]): number => {
  const peak = series.reduce(
    (acc, { metric }) => metric.timeSeries.dataPoints.reduce((innerAcc, dp) => Math.max(innerAcc, dp.value), acc),
    0
  );
  if (peak <= 0) {
    return 100;
  }
  // Add 20% headroom above the peak and round up the result, then cap at 100%.
  const withHeadroom = peak * 1.2;
  const step = withHeadroom <= 10 ? 1 : withHeadroom <= 50 ? 5 : 10;
  return Math.min(100, Math.ceil(withHeadroom / step) * step);
};

const buildSingleAirlineOptions = (categories: number[], yAxisMax: number, i18n: I18nChildContext): ApexOptions => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  plotOptions: {
    bar: {
      columnWidth: "60%",
      dataLabels: {
        position: "top",
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: { show: false },
  colors: ["var(--bui_color_action_foreground)"],
  xaxis: {
    type: "category",
    categories,
    tickPlacement: "on",
    tickAmount: CHART_DATE_TICK_AMOUNT,
    title: {
      text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_xaxis")),
      offsetY: 24,
    },
    labels: {
      rotate: 0,
      rotateAlways: false,
      formatter: formatChartDateAxisLabel,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: { show: false },
    tooltip: { enabled: false },
  },
  yaxis: {
    min: 0,
    max: yAxisMax,
    labels: {
      formatter: formatPercent,
      padding: 40,
    },
  },
  grid: {
    borderColor: "var(--bui_color_border_alt)",
    strokeDashArray: 0,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 24, left: 32, right: 24, bottom: 20 },
  },
  tooltip: {
    shared: false,
    intersect: true,
    x: { format: "d MMM yyyy" },
    y: {
      title: {
        formatter: () =>
          i18n
            .trans(
              t("flights_airline_portal_realtime_performance_metric_three_data_point_label", {
                variables: { percent: "" },
              })
            )
            .replace(": %", ":"),
      },
      formatter: formatPercent,
    },
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: "top",
    horizontalAlign: "right",
    markers: { size: 7, shape: "circle" },
    offsetY: 0,
    onItemClick: { toggleDataSeries: false },
  },
});

const buildMultiAirlineOptions = (
  categories: number[],
  yAxisMax: number,
  i18n: I18nChildContext
): ApexOptions => ({
  chart: {
    type: "bar",
    stacked: false,
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  plotOptions: {
    bar: {
      columnWidth: "60%",
      dataLabels: {
        position: "top",
      },
    },
  },
  dataLabels: { enabled: false },
  stroke: { show: false },
  colors: AIRLINE_SERIES_COLORS,
  xaxis: {
    type: "category",
    categories,
    tickPlacement: "on",
    tickAmount: CHART_DATE_TICK_AMOUNT,
    title: {
      text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_xaxis")),
      offsetY: 24,
    },
    labels: {
      rotate: 0,
      rotateAlways: false,
      formatter: formatChartDateAxisLabel,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: { show: false },
    tooltip: { enabled: false },
  },
  yaxis: {
    min: 0,
    max: yAxisMax,
    labels: {
      formatter: formatPercent,
      padding: 40,
    },
  },
  grid: {
    borderColor: "var(--bui_color_border_alt)",
    strokeDashArray: 0,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 0, left: 32, right: 24, bottom: 20 },
  },
  tooltip: {
    shared: false,
    intersect: true,
    x: { format: "d MMM yyyy" },
    custom: ({ series: seriesValues, dataPointIndex, w }) => {
      const rows = w.globals.seriesNames.map((name: string, index: number) => ({
        name,
        percentage: seriesValues[index]?.[dataPointIndex] ?? undefined,
        color: w.globals.colors[index],
      }));
      return renderToStaticMarkup(
        createElement(SearchCoverageTooltip, {
          title: formatChartDate(categories[dataPointIndex]),
          rows,
        })
      );
    },
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: "top",
    horizontalAlign: "right",
    markers: { size: 7, shape: "circle" },
    offsetY: 0,
    onItemClick: { toggleDataSeries: false },
  },
});

type UseSearchCoverageChartControllerParams = {
  series: SearchCoverageAirlineSeries[];
  showAirlineLabel?: boolean;
};

export const useSearchCoverageChartController = ({
  series,
  showAirlineLabel = false,
}: UseSearchCoverageChartControllerParams) => {
  const i18n = useI18n();
  const multiAirline = series.length > 1;
  const [firstAirlineSeries] = series;
  const averageCoverage = series.length > 1 ? undefined : Math.round(
    series.reduce((sum, { metric }) => sum + metric.average.value, 0) / series.length
  );

  const categories = useMemo(() => {
    if (!multiAirline) {
      return firstAirlineSeries.metric.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime());
    }
    const times = new Set<number>();
    series.forEach(({ metric }) =>
      metric.timeSeries.dataPoints.forEach((dp) => times.add(new Date(dp.time).getTime()))
    );
    return [...times].sort((a, b) => a - b);
  }, [series, multiAirline, firstAirlineSeries]);

  const yAxisMax = useMemo(() => computeYAxisMax(series), [series]);

  const chartSeries: ApexAxisChartSeries = useMemo(() => {
    if (!multiAirline) {
      const name = showAirlineLabel
        ? `${firstAirlineSeries.airlineName} (${firstAirlineSeries.airlineCode})`
        : i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_data_key"));
      return [
        {
          name,
          data: firstAirlineSeries.metric.timeSeries.dataPoints.map((dp) => roundToOneDecimal(dp.value)),
        },
      ];
    }
    return series.map(({ airlineCode, airlineName, metric }) => {
      const valueByTime = new Map(metric.timeSeries.dataPoints.map((dp) => [new Date(dp.time).getTime(), dp.value]));
      return {
        name: `${airlineName} (${airlineCode})`,
        data: categories.map((time) => {
          const value = valueByTime.get(time);
          return value === undefined ? null : roundToOneDecimal(value);
        }),
      };
    });
  }, [series, categories, multiAirline, i18n, firstAirlineSeries, showAirlineLabel]);

  const options: ApexOptions = useMemo(
    () =>
      multiAirline
        ? buildMultiAirlineOptions(categories, yAxisMax, i18n)
        : buildSingleAirlineOptions(categories, yAxisMax, i18n),
    [categories, multiAirline, yAxisMax, i18n]
  );

  return {
    averageCoverage,
    options,
    series: chartSeries,
  };
};
